import { format } from "date-fns";
import { ja } from "date-fns/locale";
import type { ScheduleConfig } from "../../hooks";
import { Button } from "../ui/button";

interface ScheduleConfigSectionProps {
  /** 未作成なら null */
  scheduleConfig: ScheduleConfig | null;
  onEdit: () => void;
}

export function ScheduleConfigSection({
  scheduleConfig,
  onEdit,
}: ScheduleConfigSectionProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <h2 className="font-bold text-2xl text-[var(--color-primary-500)]">
          日程調整設定
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={onEdit}
          className="text-[var(--color-primary-500)]"
        >
          編集
        </Button>
      </div>
      <div className="p-6">
        {scheduleConfig ? (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                  所要時間
                </p>
                <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                  {scheduleConfig.durationMinutes}分
                </p>
              </div>
              <div>
                <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                  勤務時間
                </p>
                <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                  {scheduleConfig.workingHoursStart} -{" "}
                  {scheduleConfig.workingHoursEnd}
                </p>
              </div>
              {scheduleConfig.deadlineDate && (
                <div>
                  <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                    回答期限
                  </p>
                  <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                    {format(
                      new Date(scheduleConfig.deadlineDate),
                      "yyyy年M月d日",
                      { locale: ja },
                    )}
                  </p>
                </div>
              )}
              {scheduleConfig.reminderEnabled && (
                <div>
                  <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                    実施前リマインド
                  </p>
                  <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                    {[
                      scheduleConfig.reminderOneWeekBefore && "実施日の1週間前",
                      scheduleConfig.reminderOneDayBefore && "実施日の前日",
                    ]
                      .filter(Boolean)
                      .join("・") || "なし"}
                  </p>
                </div>
              )}
              <div>
                <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                  日程調整枠の有効期間
                </p>
                <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                  {scheduleConfig.availableFromDays ?? 1}日後 〜{" "}
                  {scheduleConfig.availableUntilDays ?? 30}日後
                </p>
              </div>
              {(scheduleConfig.slotDisplayStartDate ||
                scheduleConfig.slotDisplayEndDate) && (
                <div>
                  <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                    日程調整枠の表示期間
                  </p>
                  <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                    {scheduleConfig.slotDisplayStartDate ?? "指定なし"} 〜{" "}
                    {scheduleConfig.slotDisplayEndDate ?? "指定なし"}
                  </p>
                </div>
              )}
              {scheduleConfig.replyToEmail && (
                <div>
                  <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                    メール返信先 (Reply-To)
                  </p>
                  <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                    {scheduleConfig.replyToEmail}
                  </p>
                </div>
              )}
            </div>
            {/* 件名・本文はメール全文を含むため全幅・縦積みで改行を保持して表示する */}
            <div>
              <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                日程調整時のタイトル
              </p>
              <p className="whitespace-pre-wrap break-words font-medium text-[15px] text-[var(--color-text-primary)]">
                {scheduleConfig.scheduleTitle}
              </p>
            </div>
            {scheduleConfig.scheduleDescription && (
              <div>
                <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                  日程調整時の本文
                </p>
                <p className="whitespace-pre-wrap break-words rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-subtle-gray)] p-3 font-medium text-[15px] text-[var(--color-text-primary)]">
                  {scheduleConfig.scheduleDescription}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-[var(--color-text-secondary)]">
            スケジュール設定がありません
          </p>
        )}
      </div>
    </div>
  );
}
